'use client';

import {
  MarkdownPreview,
  Text,
  IconButton,
  Tooltip,
} from '@terra/ui-design-system';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { SparklesIcon } from '@terra/ui-icons';
import type { MouseEvent, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  SKILL_BODY_MAX_BYTES,
  SKILL_DESCRIPTION_MAX_LENGTH,
  SKILL_NAME_MAX_LENGTH,
  SKILL_SLUG_PATTERN,
} from '@terra/shared-contracts-ai-skills';
import type { SkillsEditorLabels } from '../types';

export type SkillEditorFormDraft = {
  slug: string;
  name: string;
  description: string;
  body: string;
};
export type SkillEditorTouchedFields = {
  slug: boolean;
  name: boolean;
  description: boolean;
  body: boolean;
};
export type SkillEditorField = 'slug' | 'name' | 'description' | 'body';
export function utf8ByteLength(value: string): number {
  let bytes = 0;

  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);

    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff) {
      bytes += 4;
      index += 1;
    } else bytes += 3;
  }

  return bytes;
}

export function getSkillEditorValidation({
  draft,
  touched,
  mode,
  labels,
}: {
  draft: SkillEditorFormDraft;
  touched: SkillEditorTouchedFields;
  mode: 'create' | 'edit';
  labels: SkillsEditorLabels;
}) {
  const slugError =
    mode === 'create' &&
    (touched.slug || draft.slug !== '') &&
    (draft.slug.length < 3 || draft.slug.length > 64)
      ? labels.slug.errorLength
      : mode === 'create' &&
          (touched.slug || draft.slug !== '') &&
          !SKILL_SLUG_PATTERN.test(draft.slug)
        ? labels.slug.errorPattern
        : undefined;
  const nameError =
    touched.name && draft.name.trim().length === 0
      ? labels.name.errorRequired
      : draft.name.length > SKILL_NAME_MAX_LENGTH
        ? labels.name.errorTooLong(SKILL_NAME_MAX_LENGTH)
        : undefined;
  const descriptionError =
    touched.description && draft.description.trim().length === 0
      ? labels.summary.errorRequired
      : draft.description.length > SKILL_DESCRIPTION_MAX_LENGTH
        ? labels.summary.errorTooLong(SKILL_DESCRIPTION_MAX_LENGTH)
        : undefined;
  const bodyByteLength = utf8ByteLength(draft.body);
  const bodyError =
    touched.body && draft.body.trim().length === 0
      ? labels.body.errorRequired
      : bodyByteLength > SKILL_BODY_MAX_BYTES
        ? labels.body.errorTooLong
        : undefined;
  const hasBlockingErrors = Boolean(
    (mode === 'create' && (draft.slug.trim().length === 0 || slugError)) ||
      draft.name.trim().length === 0 ||
      nameError ||
      draft.description.trim().length === 0 ||
      descriptionError ||
      draft.body.trim().length === 0 ||
      bodyError,
  );

  return {
    slugError,
    nameError,
    descriptionError,
    bodyError,
    bodyByteLength,
    hasBlockingErrors,
  };
}

export function SkillEditorForm({
  mode,
  draft,
  labels,
  touched,
  updateEffect,
  onRefineField,
}: {
  mode: 'create' | 'edit';
  draft: SkillEditorFormDraft;
  labels: SkillsEditorLabels;
  touched: SkillEditorTouchedFields;
  updateEffect?: {
    fields: SkillEditorField[];
    timestamp: number;
  };
  onRefineField: (args: {
    field: SkillEditorField;
    label: string;
    value: string;
    isSelection: boolean;
  }) => void;
}) {
  const validation = useMemo(
    () =>
      getSkillEditorValidation({
        draft,
        touched,
        mode,
        labels,
      }),
    [draft, labels, mode, touched],
  );

  return (
    <VStack gap={3} alignItems="stretch" w="full">
      <Section
        title={labels.identitySectionTitle}
        helperText={labels.identitySectionHelp}
      >
        <VStack gap={2.5} alignItems="stretch">
          {mode === 'create' ? (
            <FieldRow
              field="slug"
              label={labels.slug.title}
              helperText={labels.slug.help}
              error={validation.slugError}
              value={draft.slug}
              emptyText={labels.emptyField}
              refineLabel={labels.refineField}
              updateEffect={updateEffect}
              onRefineField={onRefineField}
            />
          ) : null}
          <FieldRow
            field="name"
            label={labels.name.title}
            helperText={labels.name.help}
            error={validation.nameError}
            value={draft.name}
            emptyText={labels.emptyField}
            refineLabel={labels.refineField}
            updateEffect={updateEffect}
            onRefineField={onRefineField}
          />
          <FieldRow
            field="description"
            label={labels.summary.title}
            helperText={labels.summary.help}
            error={validation.descriptionError}
            value={draft.description}
            emptyText={labels.emptyField}
            refineLabel={labels.refineField}
            updateEffect={updateEffect}
            onRefineField={onRefineField}
            minH="72px"
            preserveWhitespace
            footer={
              <HStack justifyContent="flex-end" mt={0.5}>
                <Text fontSize="2xs" color="fg.muted">
                  {labels.summary.charCount(
                    draft.description.length,
                    SKILL_DESCRIPTION_MAX_LENGTH,
                  )}
                </Text>
              </HStack>
            }
          />
        </VStack>
      </Section>

      <Section
        title={labels.instructionsSectionTitle}
        helperText={labels.instructionsSectionHelp}
      >
        <VStack gap={1.5} alignItems="stretch">
          <FieldRow
            field="body"
            label={labels.body.title}
            helperText={labels.body.help}
            error={validation.bodyError}
            value={draft.body}
            emptyText={labels.previewEmpty}
            refineLabel={labels.refineField}
            updateEffect={updateEffect}
            onRefineField={onRefineField}
            minH="38vh"
            footer={
              <HStack justifyContent="flex-end" mt={0.5}>
                <Text fontSize="2xs" color="fg.muted">
                  {labels.body.byteCount(
                    validation.bodyByteLength,
                    SKILL_BODY_MAX_BYTES,
                  )}
                </Text>
              </HStack>
            }
          >
            {draft.body.trim().length === 0 ? (
              <Text fontSize="sm" color="fg.muted">
                {labels.previewEmpty}
              </Text>
            ) : (
              <MarkdownPreview source={draft.body} />
            )}
          </FieldRow>
        </VStack>
      </Section>
    </VStack>
  );
}

function FieldRow({
  field,
  label,
  helperText,
  error,
  value,
  emptyText,
  refineLabel,
  updateEffect,
  minH,
  preserveWhitespace,
  footer,
  children,
  onRefineField,
}: {
  field: SkillEditorField;
  label: string;
  helperText?: string;
  error?: string;
  value: string;
  emptyText: string;
  refineLabel: string;
  updateEffect?: {
    fields: SkillEditorField[];
    timestamp: number;
  };
  minH?: string;
  preserveWhitespace?: boolean;
  footer?: ReactNode;
  children?: ReactNode;
  onRefineField: (args: {
    field: SkillEditorField;
    label: string;
    value: string;
    isSelection: boolean;
  }) => void;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const selectedTextRef = useRef('');
  const pointerPositionRef = useRef<{
    x: number;
    y: number;
  } | null>(null);
  const selectionActionTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [contextAction, setContextAction] = useState<{
    x: number;
    y: number;
    selectedText: string;
    trigger: 'selection' | 'context-menu';
  } | null>(null);
  const [isUpdateAnimating, setIsUpdateAnimating] = useState(false);
  const syncSelectedText = useCallback(() => {
    selectedTextRef.current = getSelectedTextWithin(contentRef.current);
  }, []);
  const clearSelectionActionTimeout = useCallback(() => {
    if (!selectionActionTimeoutRef.current) {
      return;
    }

    clearTimeout(selectionActionTimeoutRef.current);
    selectionActionTimeoutRef.current = null;
  }, []);
  const showSelectionAction = useCallback(
    (fallbackPosition?: { x: number; y: number }) => {
      const selectionContext = getSelectionContextWithin(
        contentRef.current,
        fallbackPosition,
      );

      selectedTextRef.current = selectionContext?.selectedText ?? '';
      if (!selectionContext) {
        setContextAction(null);

        return;
      }

      setContextAction({
        x: selectionContext.x,
        y: selectionContext.y,
        selectedText: selectionContext.selectedText,
        trigger: 'selection',
      });
    },
    [],
  );
  const scheduleSelectionAction = useCallback(
    (fallbackPosition?: { x: number; y: number }) => {
      clearSelectionActionTimeout();
      selectionActionTimeoutRef.current = setTimeout(() => {
        showSelectionAction(fallbackPosition);
        selectionActionTimeoutRef.current = null;
      }, 1000);
    },
    [clearSelectionActionTimeout, showSelectionAction],
  );
  const scheduleSelectionActionFromCurrentSelection = useCallback(
    (fallbackPosition?: { x: number; y: number }) => {
      window.setTimeout(() => {
        const selectionContext = getSelectionContextWithin(
          contentRef.current,
          fallbackPosition,
        );

        selectedTextRef.current = selectionContext?.selectedText ?? '';
        if (selectionContext) {
          scheduleSelectionAction(fallbackPosition);

          return;
        }

        clearSelectionActionTimeout();
        setContextAction(null);
      }, 0);
    },
    [clearSelectionActionTimeout, scheduleSelectionAction],
  );
  const handleMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    pointerPositionRef.current = { x: event.clientX, y: event.clientY };
    clearSelectionActionTimeout();
    setContextAction(null);
  };

  const handleMouseUp = (event: MouseEvent<HTMLDivElement>) => {
    pointerPositionRef.current = { x: event.clientX, y: event.clientY };
    scheduleSelectionActionFromCurrentSelection(pointerPositionRef.current);
  };

  const handleRefine = (overrideSelectedText?: string) => {
    const selectedText =
      overrideSelectedText ??
      (getSelectedTextWithin(contentRef.current) || selectedTextRef.current);

    setContextAction(null);
    onRefineField({
      field,
      label,
      value: selectedText || value,
      isSelection: selectedText.length > 0,
    });
  };

  const handleContextMenu = (event: MouseEvent<HTMLDivElement>) => {
    clearSelectionActionTimeout();
    syncSelectedText();
    const selectedText = selectedTextRef.current || value.trim();

    if (!selectedText) {
      return;
    }

    event.preventDefault();
    setContextAction({
      x: event.clientX,
      y: event.clientY,
      selectedText,
      trigger: 'context-menu',
    });
  };

  useEffect(() => {
    if (!contextAction) {
      return;
    }

    const closeContextAction = () => setContextAction(null);
    const timeout = window.setTimeout(() => {
      document.addEventListener('click', closeContextAction);
    }, 0);

    document.addEventListener('keydown', closeContextAction);

    return () => {
      window.clearTimeout(timeout);
      document.removeEventListener('click', closeContextAction);
      document.removeEventListener('keydown', closeContextAction);
    };
  }, [contextAction]);
  useEffect(() => {
    const handleDocumentSelection = (event: Event) => {
      const fallbackPosition =
        event instanceof globalThis.MouseEvent
          ? { x: event.clientX, y: event.clientY }
          : (pointerPositionRef.current ?? undefined);

      if (fallbackPosition) {
        pointerPositionRef.current = fallbackPosition;
      }

      scheduleSelectionActionFromCurrentSelection(fallbackPosition);
    };

    document.addEventListener('selectionchange', handleDocumentSelection);
    document.addEventListener('mouseup', handleDocumentSelection);
    document.addEventListener('keyup', handleDocumentSelection);

    return () => {
      document.removeEventListener('selectionchange', handleDocumentSelection);
      document.removeEventListener('mouseup', handleDocumentSelection);
      document.removeEventListener('keyup', handleDocumentSelection);
    };
  }, [scheduleSelectionActionFromCurrentSelection]);
  useEffect(() => {
    return () => clearSelectionActionTimeout();
  }, [clearSelectionActionTimeout]);
  useEffect(() => {
    if (!updateEffect?.fields.includes(field)) {
      return;
    }

    setIsUpdateAnimating(true);
    const timeout = window.setTimeout(() => {
      setIsUpdateAnimating(false);
    }, 1800);

    return () => window.clearTimeout(timeout);
  }, [field, updateEffect?.fields, updateEffect?.timestamp]);
  const hasValue = value.trim().length > 0;

  return (
    <VStack alignItems="stretch" gap={1.5} role="group">
      <HStack justifyContent="space-between" gap={2} alignItems="flex-start">
        <VStack alignItems="flex-start" gap={0.5}>
          <Text fontSize="sm" fontWeight="medium" color="fg.default">
            {label}
          </Text>
          {helperText ? (
            <Text fontSize="xs" color="fg.muted" lineHeight="1.5">
              {helperText}
            </Text>
          ) : null}
        </VStack>
        <Tooltip
          title={refineLabel}
          positioning={{ strategy: 'fixed', placement: 'top' }}
        >
          <IconButton
            aria-label={refineLabel}
            size="xs"
            variant="ghost"
            flexShrink={0}
            color="fg.muted"
            _hover={{ color: 'fg.default', bg: 'bg.hover' }}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => handleRefine()}
          >
            <SparklesIcon size={13} />
          </IconButton>
        </Tooltip>
      </HStack>
      <Box
        ref={contentRef}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onKeyUp={syncSelectedText}
        onContextMenu={handleContextMenu}
        borderRadius="sm"
        borderWidth="1px"
        borderStyle="solid"
        borderColor={
          error
            ? 'border.error'
            : isUpdateAnimating
              ? 'border.info'
              : 'border.default'
        }
        bg={isUpdateAnimating ? 'bg.muted' : 'bg.default'}
        px={3.5}
        py={3}
        minH={minH ?? '40px'}
        transition="background-color 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease"
        boxShadow={
          isUpdateAnimating ? '0 0 0 2px rgba(59, 130, 246, 0.28)' : undefined
        }
      >
        {children ??
          (hasValue ? (
            <Text
              fontSize="sm"
              color="fg.default"
              lineHeight="1.55"
              whiteSpace={preserveWhitespace ? 'pre-wrap' : undefined}
            >
              {value}
            </Text>
          ) : (
            <Text fontSize="sm" color="fg.muted" lineHeight="1.55">
              {emptyText}
            </Text>
          ))}
      </Box>
      {contextAction ? (
        <button
          type="button"
          aria-label={refineLabel}
          style={{
            position: 'fixed',
            left: contextAction.x,
            top: contextAction.y,
            zIndex: 9999,
            display: 'inline-flex',
            alignItems: 'center',
            gap: contextAction.trigger === 'selection' ? 0 : 6,
            minWidth: contextAction.trigger === 'selection' ? 28 : undefined,
            height: 28,
            padding: contextAction.trigger === 'selection' ? 0 : '0 10px',
            justifyContent: 'center',
            borderRadius: 8,
            border: '1px solid var(--colors-border-default, #d0d5dd)',
            background: 'var(--colors-bg-default, #fff)',
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.18)',
            color: 'var(--colors-fg-default, #111827)',
            cursor: 'pointer',
          }}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => handleRefine(contextAction.selectedText)}
        >
          <SparklesIcon size={13} />
          {contextAction.trigger === 'context-menu' ? refineLabel : null}
        </button>
      ) : null}
      {footer}
      {error ? (
        <Text fontSize="xs" color="fg.error">
          {error}
        </Text>
      ) : null}
    </VStack>
  );
}

function Section({
  title,
  helperText,
  children,
}: {
  title: string;
  helperText?: string;
  children: ReactNode;
}) {
  return (
    <VStack
      alignItems="stretch"
      gap={3}
      borderRadius="sm"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="border.default"
      bg="bg.subtle"
      p={4}
    >
      <VStack alignItems="flex-start" gap={0.5}>
        <Text
          fontSize="xs"
          fontWeight="semibold"
          color="fg.default"
          textTransform="uppercase"
          letterSpacing="wider"
        >
          {title}
        </Text>
        {helperText ? (
          <Text fontSize="xs" color="fg.muted" lineHeight="1.5">
            {helperText}
          </Text>
        ) : null}
      </VStack>
      {children}
    </VStack>
  );
}

function getSelectedTextWithin(container: HTMLElement | null): string {
  if (!container || typeof window === 'undefined') {
    return '';
  }

  const selection = window.getSelection();

  if (!selection || selection.rangeCount === 0) {
    return '';
  }

  const selectedText = selection.toString().trim();

  if (!selectedText) {
    return '';
  }

  const anchorNode = selection.anchorNode;
  const focusNode = selection.focusNode;
  const range = selection.getRangeAt(0);
  const isSelectionInsideContainer =
    Boolean(anchorNode && container.contains(anchorNode)) ||
    Boolean(focusNode && container.contains(focusNode)) ||
    doesRangeIntersectContainer({ range, container });

  if (!isSelectionInsideContainer) {
    return '';
  }

  return selectedText;
}

function getSelectionContextWithin(
  container: HTMLElement | null,
  fallbackPosition?: {
    x: number;
    y: number;
  },
): {
  selectedText: string;
  x: number;
  y: number;
} | null {
  if (!container || typeof window === 'undefined') {
    return null;
  }

  const selection = window.getSelection();

  if (!selection || selection.rangeCount === 0) {
    return null;
  }

  const selectedText = getSelectedTextWithin(container);

  if (!selectedText) {
    return null;
  }

  const range = selection.getRangeAt(0);
  const focusRect = getSelectionFocusRect({ selection, container });
  const rects = Array.from(range.getClientRects()).filter(
    (rect) => rect.width > 0 && rect.height > 0,
  );
  const anchorRect =
    focusRect ?? rects[rects.length - 1] ?? range.getBoundingClientRect();

  if (
    (!anchorRect || (anchorRect.width === 0 && anchorRect.height === 0)) &&
    !fallbackPosition
  ) {
    return null;
  }

  const buttonSize = 28;
  const gap = 6;
  const anchorX = fallbackPosition?.x ?? anchorRect.right;
  const anchorY = fallbackPosition?.y ?? anchorRect.top + anchorRect.height / 2;
  const preferredX = anchorX + gap;
  const fallbackX = anchorX - buttonSize - gap;
  const x = Math.min(
    Math.max(
      gap,
      preferredX > window.innerWidth - buttonSize - gap
        ? fallbackX
        : preferredX,
    ),
    window.innerWidth - buttonSize - gap,
  );
  const preferredY = anchorY - buttonSize / 2;
  const y = Math.min(
    Math.max(gap, preferredY),
    window.innerHeight - buttonSize - gap,
  );

  return { selectedText, x, y };
}

function getSelectionFocusRect({
  selection,
  container,
}: {
  selection: Selection;
  container: HTMLElement;
}): DOMRect | null {
  const focusNode = selection.focusNode;

  if (!focusNode || !container.contains(focusNode)) {
    return null;
  }

  const focusRange = document.createRange();

  try {
    focusRange.setStart(focusNode, selection.focusOffset);
    focusRange.collapse(true);
    const focusRect = focusRange.getBoundingClientRect();

    if (focusRect.width > 0 || focusRect.height > 0) {
      return focusRect;
    }
  } catch {
    return null;
  }

  const rects = Array.from(selection.getRangeAt(0).getClientRects()).filter(
    (rect) => rect.width > 0 && rect.height > 0,
  );

  return rects[rects.length - 1] ?? null;
}

function doesRangeIntersectContainer({
  range,
  container,
}: {
  range: Range;
  container: HTMLElement;
}): boolean {
  try {
    return range.intersectsNode(container);
  } catch {
    return false;
  }
}
